import { useMemo } from 'react';
import { CalendarClock } from 'lucide-react';
import { Skeleton, Badge } from '@vnetwork/ui/primitives';
import { StatusPill, EmptyState } from '@vnetwork/ui/data';
import { useSpecialWorkdays, type SpecialWorkday } from '@/api/dashboard-scheduling';
import { formatDayMonth, weekdayShort } from '@/lib/format';
import { WidgetCard } from './WidgetCard';

/**
 * Special work schedule (mockup: Lịch làm việc đặc biệt). REAL month workdays
 * from /workdays/holiday/... — remote-work days, Saturday-off, holidays. Shows
 * upcoming entries first, tagged by type; this-week entries are highlighted.
 */

function isRemote(name: string): boolean {
  return /remote|wfh|từ xa/i.test(name);
}

/** Days-from-today (0 = today, negative = past). */
function dayDelta(iso: string): number {
  const d = new Date(iso);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  d.setHours(0, 0, 0, 0);
  return Math.round((d.getTime() - today.getTime()) / 86_400_000);
}

export function WorkScheduleWidget() {
  const now = new Date();
  const { data = [], isLoading } = useSpecialWorkdays(now.getFullYear(), now.getMonth() + 1);

  // Upcoming first (today onwards), then recent past — soonest at the top.
  const rows = useMemo<SpecialWorkday[]>(() => {
    const withDelta = data.map((d) => ({ d, delta: dayDelta(d.holiday_date) }));
    const upcoming = withDelta.filter((x) => x.delta >= 0).sort((a, b) => a.delta - b.delta);
    const past = withDelta.filter((x) => x.delta < 0).sort((a, b) => b.delta - a.delta);
    return [...upcoming, ...past].slice(0, 7).map((x) => x.d);
  }, [data]);

  return (
    <WidgetCard
      title="Work Schedule"
      description="Special workdays this month"
      actions={<CalendarClock size={16} color="var(--vn-fg-muted)" />}
    >
      {isLoading ? (
        <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} style={{ height: 40 }} />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <EmptyState size="sm" title="No special workdays" description="Remote days & holidays will show here." />
      ) : (
        <div style={{ display: 'grid', gap: 'var(--vn-space-1)' }}>
          {rows.map((w) => {
            const delta = dayDelta(w.holiday_date);
            const isPast = delta < 0;
            const isThisWeek = delta >= 0 && delta < 7;
            const remote = isRemote(w.holiday_name);
            return (
              <div
                key={w.holiday_id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--vn-space-3)',
                  padding: 'var(--vn-space-2) var(--vn-space-3)',
                  borderRadius: 'var(--vn-radius-md)',
                  background: isThisWeek ? 'var(--vn-accent-subtle)' : 'transparent',
                  opacity: isPast ? 0.55 : 1,
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    minWidth: 44,
                    lineHeight: 1.15,
                  }}
                >
                  <span style={{ fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-muted)' }}>
                    {weekdayShort(w.holiday_date)}
                  </span>
                  <span style={{ fontSize: 'var(--vn-font-size-sm)', fontWeight: 'var(--vn-font-weight-semibold)' }}>
                    {formatDayMonth(w.holiday_date)}
                  </span>
                </div>
                <span
                  style={{
                    flex: 1,
                    minWidth: 0,
                    fontSize: 'var(--vn-font-size-sm)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {w.holiday_name}
                </span>
                <Badge tone={remote ? 'info' : 'success'} appearance="subtle" size="sm">
                  {remote ? 'Remote' : 'Holiday'}
                </Badge>
                {isThisWeek ? (
                  <StatusPill tone="warning">This week</StatusPill>
                ) : null}
              </div>
            );
          })}
        </div>
      )}
    </WidgetCard>
  );
}
